/* eslint-disable max-len */
/* eslint-disable no-console */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Input from '@ziynx/components/inputs';
import { Button } from '@ziynx/components/buttons';
import { Helmet } from 'react-helmet-async';
import {
  useDispatch, useSelector, shallowEqual,
} from 'react-redux';
import { useParams } from 'react-router-dom';
import { ContentContainer } from '@ziynx/components/layout';
import Link from '@ziynx/components/links';
import Colors from '@ziynx/theme/colors';
import { Form, FormRow } from '@ziynx/components/forms';
import Dropdown from '@ziynx/components/selects';
import { Body } from '@ziynx/components/typography';
import ActionTypes from '@ziynx/action-types';
import type { Viewports } from '@ziynx/flow-types/globals';
import { withAppManager, useNotification, LoadingSpinner } from '@ziynx/components/utilities';
import { TextEditor } from '@krispy-web/text-editor';
import * as KrispyTokens from '@krispy-tokens/blue';

export interface Message {
  htmlString?: string;
  jsonState?: string;
}

type Props = $ReadOnly<{|
  /**
   * Viewport
   */
  viewport: Viewports,
  /**
   * Navigation stack
   */
  history: {
    push: (pathName: string, params?: { from: string }) => void,
    goBack: () => void,
    location: {
      state: {
        from: string,
      }
    },
  },

|}>;

const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '40px 16px 60px' : '40px 40px 60px')};
`;

const PageContentWrapper = styled.div`
  width: 100%;
  max-width: 688px;
`;

const LinkWrapper = styled.div`
  margin-bottom: 24px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
`;

const StyledButton = styled(Button)`
  width: ${({ isMobileSmall }) => (isMobileSmall ? '100%' : '50%')};
  &:first-of-type {
    ${({ isMobileSmall }) => (isMobileSmall ? 'margin-bottom: 16px;' : 'margin-right: 16px;')}
  }
`;

const BodyWrapper = styled.div`
  padding-bottom: ${KrispyTokens.KrispySpacingElementsMdSmall}px;
`;

const TextEditorWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  height: 100%;
  --text-editor-max-height-mobile: 180px;
  --text-editor-max-height-tablet-up: 262px;

  > div {
    width: 100%;
    border: 1px solid transparent;
    border-radius: 16px;
    min-height: 82px;
    min-width: 254px;
    a {
      color: ${KrispyTokens.KrispyColorElementsAccentOndark};
    }

    p {
      margin: 0;
    }
    i {
      font-style: italic;
    }
    
    :global .strikethrough {
      text-decoration: line-through;
    }
    
    ul {
      margin: 0;
      font-family: inherit;
      padding-inline-start: calc(${KrispyTokens.KrispySpacingElementsHdLarge}px + 8px);
    }
    
    li {
      font-family: inherit;
    }

    &:focus-visible, &:focus {
      outline: none;
      border: none;
    }
  }
`;

const UNKNOWN_ITEM = {
  name: 'Unknown',
  value: 'unknown',
};

const TalentList = (props: ManagedProps<Props>) => {
  const { viewport, history } = props;
  const { talentId } = useParams();

  const { notificationDispatch } = useNotification();

  const isMobileSmall = viewport === 'mobileSmall';

  // eslint-disable-next-line max-len
  const scorecardService = useSelector(({ adminTalentScorecard = {} }) => (adminTalentScorecard[talentId] || {}), shallowEqual);
  const scorecardValuesService = useSelector(({ adminTalentScorecardValues = {} }) => ({
    jobStatus: [UNKNOWN_ITEM, ...(adminTalentScorecardValues?.jobStatus || []).map((item) => ({
      value: item?._id,
      name: item?.name,
    }))],
    rating: [UNKNOWN_ITEM, ...(adminTalentScorecardValues?.rating || []).map((item) => ({
      value: item?._id,
      name: item?.name,
    }))],
    jobType: [UNKNOWN_ITEM, ...(adminTalentScorecardValues?.jobType || []).map((item) => ({
      value: item?._id,
      name: item?.name,
    }))],
  }));

  const [talentNoteString, setTalentNoteString] = useState(scorecardService?.talentNotes || '');

  const dispatch: DispatchAction = useDispatch();

  useEffect(() => {
    dispatch({
      type: ActionTypes.admin.getTalentScorecard.START,
      id: talentId,
    });

    dispatch({
      type: ActionTypes.admin.getTalentScorecardValues.START,
    });
  }, []);

  useEffect(() => {
    setTalentNoteString(scorecardService?.talentNotes);
  }, [scorecardService?.talentNotes]);

  const _onSubmit = async (evt: React.SyntheticEvent) => {
    evt.preventDefault();
    if (scorecardService?.fetching || scorecardService?.isPatching) return;
    const target = evt?.target;
    const ratingValue = target?.ratingType?.value;
    const jobStatusValue = target?.jobStatus?.value;
    const jobTypeValue = target?.jobType?.value;

    if (!ratingValue || !jobStatusValue || !jobTypeValue) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Please fill out all required fields.',
        },
      });
    }

    if (!talentId) history.back();

    const ops = [];

    // rating set to unknown
    if (ratingValue === 'unknown') {
      ops.push({ op: 'remove', path: 'rating' });
    } else if (scorecardService?.rating?._id !== ratingValue) {
      // Rating value has changed
      ops.push({ op: 'add', path: 'rating', value: ratingValue });
    }

    // jobStatus set to unknown
    if (jobStatusValue === 'unknown') {
      ops.push({ op: 'remove', path: 'jobStatus' });
    } else if (scorecardService?.jobStatus?._id !== jobStatusValue) {
      // jobStatus value has changed
      ops.push({ op: 'add', path: 'jobStatus', value: jobStatusValue });
    }

    // jobType set to unknown
    if (jobTypeValue === 'unknown') {
      ops.push({ op: 'remove', path: 'jobType' });
    } else if (scorecardService?.jobType?._id !== jobTypeValue) {
      // jobType value has changed
      ops.push({ op: 'add', path: 'jobType', value: jobTypeValue });
    }

    if (talentNoteString?.length > 0 && scorecardService?.talentNotes !== talentNoteString) {
      ops.push({ op: 'add', path: 'talentNotes', value: talentNoteString || '' });
    }

    dispatch({
      type: ActionTypes.admin.patchTalentScorecard.START,
      id: talentId,
      ops,
    });
  };

  const _onChangeTalentNote = (message: Message) => {
    if (message?.htmlString) setTalentNoteString(message?.htmlString);
  };

  return (
    <>
      <Helmet>
        <title>
          Admin Edit Talent | ZIYNX
        </title>
      </Helmet>
      <ContentContainer>
        <PageWrapper isMobileSmall={isMobileSmall}>
          <PageContentWrapper>
            <LinkWrapper>
              <Link
                variant="caret"
                onClick={() => {
                  history.goBack();
                }}
                color={Colors.GrayDark}
              >
                Back to talent list
              </Link>
            </LinkWrapper>
            { (scorecardService?.fetching) && (<LoadingWrapper><LoadingSpinner /></LoadingWrapper>) }
            { (!scorecardService.fetching) && (
              <Form
                formId="edit-admin-talent"
                title="Edit Admin Talent"
                width="100%"
                onSubmit={_onSubmit}
              >
                <FormRow>
                  <Input
                    disabled
                    variant="text"
                    autoComplete="off"
                    inputName="name"
                    label="Name"
                    defaultValue={scorecardService?.fullName ?? ''}
                  />
                </FormRow>
                <FormRow paddingBottom="32px" height="100%">
                  <Dropdown
                    required
                    activeLabel="Rating"
                    label="Rating"
                    inputName="ratingType"
                    options={scorecardValuesService?.rating}
                    disabled={scorecardService?.fetching || scorecardService?.isPatching}
                    defaultValue={scorecardService?.rating?._id || UNKNOWN_ITEM.value}
                  />
                </FormRow>
                <FormRow paddingBottom="32px" height="100%">
                  <Dropdown
                    required
                    activeLabel="Job status"
                    label="Job status"
                    inputName="jobStatus"
                    options={scorecardValuesService?.jobStatus}
                    disabled={scorecardService?.fetching || scorecardService?.isPatching}
                    defaultValue={scorecardService?.jobStatus?._id || UNKNOWN_ITEM.value}
                  />
                </FormRow>
                <FormRow paddingBottom="32px" height="100%">
                  <Dropdown
                    required
                    activeLabel="Job type"
                    label="Job type"
                    inputName="jobType"
                    options={scorecardValuesService?.jobType}
                    disabled={scorecardService?.fetching || scorecardService?.isPatching}
                    defaultValue={scorecardService?.jobType?._id || UNKNOWN_ITEM.value}
                  />
                </FormRow>
                <FormRow height="100%">
                  <BodyWrapper>
                    <Body medium color={Colors.GrayDark}>Note</Body>
                  </BodyWrapper>
                  <TextEditorWrapper>
                    <TextEditor
                      onChange={_onChangeTalentNote}
                      initialValue={scorecardService?.talentNotes}
                      editable={scorecardService?.fetching || scorecardService?.isPatching}
                      placeholderText="Type your note..."
                    />
                  </TextEditorWrapper>
                </FormRow>
                <ButtonWrapper isMobileSmall={isMobileSmall}>
                  <StyledButton
                    type="submit"
                    width="50%"
                    disabled={scorecardService?.fetching || scorecardService?.isPatching}
                    isLoading={scorecardService?.isPatching}
                    isMobileSmall={isMobileSmall}
                  >
                    Save
                  </StyledButton>
                  <StyledButton
                    isMobileSmall={isMobileSmall}
                    variant="primary"
                    width="50%"
                    inverted
                    onClick={() => {
                      if (scorecardService?.fetching || scorecardService?.isPatching) return;
                      history.goBack();
                    }}
                  >
                    Cancel
                  </StyledButton>
                </ButtonWrapper>
              </Form>
            ) }
          </PageContentWrapper>
        </PageWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(TalentList): React$ComponentType<ManagedProps<Props>>);
